import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { useAbility } from '@casl/react';
import { Button, Col, Row } from 'react-bootstrap';
import {
  debounceIndexOvertimeRequest,
  importOvertimeRequest,
  importTemplateOvertimeRequest,
  preImportOvertimeRequest
} from '../../../requests/overtimes';
import {
  SimpleCenteredModal,
  DefaultModal,
  FormikSelect,
  ComponentDataTable,
  ImportModal,
  ButtonTooltip,
  Icon
} from '../../../components';
import { AbilityContext } from '../../../config/abilityContext';
import columns from './Columns';
import OvertimeInfoTable from './OvertimeInfoTable';
import OvertimeShow from './OvertimeShow';
import OvertimeBossNew from './OvertimeBossNew';
import { useAuthorization } from '../../../services/hooks';
import { sendAlert } from '../../../actions/utils';
import AuthorizeOvertimes from './AuthorizeOvertimes';

const OvertimeProfileDatatableStyled = ({
  currentEmployee,
  defaultStatus = '0',
  employeesBelongBoss,
  moreData,
  setMoreData
}) => {
  const ability = useAbility(AbilityContext);
  const [amount, setAmount] = useState(0);
  const [defaultModalBody, setDefaultModalBody] = useState('');
  const [firstModalShow, setFirstModalShow] = useState(false);
  const [defaultModalButtons, setDefaultModalButtons] = useState(true);
  const [defaultModalSize, setDefaultModalSize] = useState('lg');
  const [overtimes, setOvertimes] = useState([]);
  const [modalBody, setModalBody] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [modalSize, setModalSize] = useState('lg');
  const [modalTitle, setModalTitle] = useState('');
  const [alertModalShow, setAlertModalShow] = useState(false);
  const [onRequest, setOnRequest] = useState(true);
  const dispatch = useDispatch();
  const [status, setStatus] = useState(defaultStatus);
  const [newForm, setNewForm] = useState(false);
  const [authorizeOvertime, setAuthorizeOvertime] = useState(false);
  const [currentCard, setCurrentCard] = useState({
    title: '',
    alertInfo: ''
  });

  const overtimeStatus = [
    { label: 'Todos', value: '' },
    { label: 'Aprobado', value: '1' },
    { label: 'Pendiente', value: '0' },
    { label: 'Rechazado', value: '2' }
  ];

  const handleSuccessIndex = response => {
    const { data, metadata } = response.data;
    setOvertimes(data);
    setAmount(metadata.amount);
    setOnRequest(false);
  };

  const handleCustomParams = () => {
    if (status !== '0' && employeesBelongBoss.length > 0) {
      return {
        filter_by_employee: employeesBelongBoss
      };
    }

    return {
      pending_review: currentEmployee
    };
  };

  const handleIndexRequest = async params => {
    const customParams = handleCustomParams(status);
    setOnRequest(true);
    const sendParams = {
      ...customParams,
      ...params,
      status
    };
    debounceIndexOvertimeRequest({ dispatch, params: sendParams, successCallback: handleSuccessIndex });
  };

  const handleDefaultModalClose = () => {
    setFirstModalShow(false);
  };

  const handleManageRequest = item => {
    setDefaultModalBody(
      <OvertimeInfoTable
        item={item}
        currentEmployee={currentEmployee}
        setModalShow={setFirstModalShow}
        moreData={moreData}
        setMoreData={setMoreData}
      />
    );
    setDefaultModalButtons(false);
    setFirstModalShow(true);
    setDefaultModalSize('xl');
  };

  const handleActions = (item, action) => {
    switch (action) {
      case 'actions':
        handleManageRequest(camelCaseRecursive(item));
        break;
      case 'show':
        setModalTitle('Solicitud de Horas Extras');
        setModalBody(<OvertimeShow item={item} />);
        setModalShow(true);
        setModalSize('md');
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
        break;
    }
  };

  const sortColumnCase = name => {
    switch (name) {
      case 'employee' || 'employee.full_name':
        return { sort_employees: name };
      case 'status':
        return { sort_status: name };
      default:
        return { sort_column: name };
    }
  };

  const managementColumns = columns(handleActions, ability, 'leadership').filter(
    column => !['APROBAR'].some(name => column.name === name)
  );

  const handleImportModalExceptions = error => {
    if (error?.response?.status === 422) {
      const alertInfo = <pre>{error?.response?.data?.message}</pre>;
      setCurrentCard({ ...currentCard, alertInfo });
      setAlertModalShow(true);
    } else {
      dispatch(sendAlert({ kind: 'error', message: error?.response?.data?.message }));
    }
  };

  const handleImport = () => {
    setModalTitle('Importar Horas Extra');
    setModalBody(
      <ImportModal
        onDropUploaded={preImportOvertimeRequest}
        handleTemplate={importTemplateOvertimeRequest}
        onHide={response => {
          importOvertimeRequest(response);
          setModalShow(false);
          setNewForm(false);
          setOnRequest(true);
          setMoreData(prev => !prev);
        }}
        hideModal={() => {
          setModalShow(false);
        }}
        updateData={() => setMoreData(!moreData)}
        handleExceptions={handleImportModalExceptions}
      />
    );
    setModalShow(true);
  };

  const renderContent = () => {
    if (authorizeOvertime) {
      return (
        <AuthorizeOvertimes
          onClose={() => setAuthorizeOvertime(false)}
          onSuccess={() => {
            setAuthorizeOvertime(false);
            setOnRequest(true);
            setMoreData(prev => !prev);
          }}
        />
      );
    }

    if (newForm) {
      return <OvertimeBossNew onSuccess={() => setNewForm(false)} />;
    }

    return (
      <ComponentDataTable
        columns={managementColumns}
        data={overtimes}
        handleSortCase={sortColumnCase}
        customHeaderCenter={
          <>
            <FormikSelect
              options={overtimeStatus}
              label=""
              value={status}
              defaultValue={status}
              onChange={data => {
                setStatus(data ? data.value : '');
                setOnRequest(true);
                setMoreData(!moreData);
              }}
              placeholder="Todos"
            />
          </>
        }
        moreData={moreData}
        nameRangePicker="filter_dates"
        onRequest={onRequest}
        rangePicker
        resourceRequest={handleIndexRequest}
        totalRows={amount}
        withEndDate
        withStartDate
      />
    );
  };

  return (
    <>
      <Row className="justify-content-between">
        <Col md="auto" className="d-flex align-items-center">
          {useAuthorization('import', 'Overtime') && newForm && (
            <ButtonTooltip
              variant="circle-primary"
              className="mr-2 mb-3 btn-circle"
              text="Importar"
              onClick={handleImport}
            >
              <Icon icon="cloud-upload-outline" />
            </ButtonTooltip>
          )}
        </Col>
        <Col md="auto" className="d-flex align-items-center justify-content-end">
          {useAuthorization('authorize', 'Overtime') && !newForm && !authorizeOvertime && (
            <Button
              variant="warning"
              className="mr-2 mt-2"
              onClick={() => {
                setAuthorizeOvertime(true);
                setNewForm(false);
              }}
            >
              Autorizar Tiempo Extra
            </Button>
          )}
          {useAuthorization('boss_create', 'Overtime') && (
            <Button
              variant="primary"
              className="mt-2"
              onClick={() => {
                // Si estamos en el formulario nuevo o en autorizar -> volver al listado
                if (newForm || authorizeOvertime) {
                  setNewForm(false);
                  setAuthorizeOvertime(false);
                } else {
                  // Si no, abrir formulario de agregar horas extra
                  setNewForm(true);
                  setAuthorizeOvertime(false);
                }
              }}
            >
              {newForm || authorizeOvertime ? 'Volver' : 'Agregar Horas Extra'}
            </Button>
          )}
        </Col>
      </Row>

      {renderContent()}

      <SimpleCenteredModal
        size={modalSize}
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        onHide={() => setModalShow(false)}
      />
      <DefaultModal
        title=""
        body={defaultModalBody}
        disabled={onRequest}
        handleClose={handleDefaultModalClose}
        modalSize={defaultModalSize}
        show={firstModalShow}
        withClose={defaultModalButtons}
        withConfirm={defaultModalButtons}
      />

      <DefaultModal
        title="Información Relevante"
        body={currentCard.alertInfo}
        show={alertModalShow}
        withClose={false}
        handleConfirm={() => setAlertModalShow(false)}
        handleClose={() => setAlertModalShow(false)}
        titleBtnSave="Confirmar"
        scrollable
      />
    </>
  );
};

export default OvertimeProfileDatatableStyled;
